import { z } from "zod/v4";

export namespace OpenFoodFactsTaxonomy {
  export type SnapshotNode = {
    tag: string;
    label: string | null;
    parentTags: string[];
    synonyms: string[];
  };

  export type Snapshot = {
    taxonomyVersion: string;
    nodes: SnapshotNode[];
  };

  export const snapshotNodeSchema = z.object({
    tag: z.string().min(1),
    label: z.string().nullable(),
    parentTags: z.array(z.string()),
    synonyms: z.array(z.string()),
  });

  export const snapshotSchema = z.object({
    taxonomyVersion: z.string().min(1),
    nodes: z.array(snapshotNodeSchema),
  });

  export type Resolver = {
    taxonomyVersion: string;
    getAncestorTags(offTag: string): string[];
    isDescendantOf(args: { descendantTag: string; ancestorTag: string }): boolean;
  };

  export function createResolver(snapshot: Snapshot): Resolver {
    const ancestorTagsByTag = buildAncestorTagsByTag(snapshot);

    return {
      taxonomyVersion: snapshot.taxonomyVersion,
      getAncestorTags(offTag: string): string[] {
        const normalizedTag = offTag.trim();
        if (!normalizedTag) {
          return [];
        }

        return [...(ancestorTagsByTag.get(normalizedTag) ?? new Set<string>())];
      },
      isDescendantOf(args: { descendantTag: string; ancestorTag: string }): boolean {
        const descendantTag = args.descendantTag.trim();
        const ancestorTag = args.ancestorTag.trim();
        if (!descendantTag || !ancestorTag) {
          return false;
        }

        return ancestorTagsByTag.get(descendantTag)?.has(ancestorTag) ?? false;
      },
    };
  }

  function buildAncestorTagsByTag(snapshot: Snapshot): Map<string, Set<string>> {
    const parentTagsByTag = new Map<string, string[]>();
    for (const node of snapshot.nodes) {
      parentTagsByTag.set(node.tag, node.parentTags);
    }

    const memo = new Map<string, Set<string>>();
    const visiting = new Set<string>();

    const visit = (tag: string): Set<string> => {
      const existing = memo.get(tag);
      if (existing) {
        return existing;
      }

      if (visiting.has(tag)) {
        return new Set([tag]);
      }

      visiting.add(tag);
      const ancestorTags = new Set<string>([tag]);
      for (const parentTag of parentTagsByTag.get(tag) ?? []) {
        ancestorTags.add(parentTag);
        for (const ancestorTag of visit(parentTag)) {
          ancestorTags.add(ancestorTag);
        }
      }
      visiting.delete(tag);
      memo.set(tag, ancestorTags);
      return ancestorTags;
    };

    for (const node of snapshot.nodes) {
      visit(node.tag);
    }

    return memo;
  }
}
